import React from 'react';
import './css/ProjectCard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faLaptop, faHeart, faServer, faTerminal, faDatabase } from '@fortawesome/free-solid-svg-icons';
import {
  faJs,
  faReact,
  faNode,
  faGit,
  faPython,
} from '@fortawesome/free-brands-svg-icons';
import { likeProject } from '../../api/likes';

function getIcon(skill) {
  const i = skill.toLowerCase();
  const icons = {
    mysql: {
      src: './assets/icons/mysql.png',
      label: 'MySQL',
      class: 'mysqlBadge',
    },
    python: {
      src: './assets/icons/python.png',
      label: 'Python',
      class: 'python-badge',
    },
    kivy: { src: './assets/icons/kivy.png', label: 'Kivy', class: 'kivyBadge' },
    node: {
      src: './assets/icons/nodejs.png',
      label: 'Node',
      class: 'nodeBadge',
    },
    vue: { src: './assets/icons/vue.png', label: 'Vue', class: 'vueBadge' },
    mongo: {
      src: './assets/icons/mongodb.png',
      label: 'Mongo',
      class: 'mongoBadge',
    },
    typescript: { label: 'TypeScript', class: 'typescriptBadge', icon: faJs },
    javascript: { label: 'JavaScript', class: 'javascriptBadge', icon: faJs },
    react: { label: 'React', class: 'reactBadge', icon: faReact },
    nodejs: { label: 'Node.js', class: 'nodeJsBadge', icon: faNode },
    postgresql: { label: 'PostgreSQL', class: 'postgresqlBadge', icon: faDatabase },
    mongodb: { label: 'MongoDB', class: 'mongoDBBadge', icon: faDatabase },
    git: { label: 'Git', class: 'gitBadge', icon: faGit },
    java: { label: 'Java', class: 'javaBadge', icon: faCode },
    csharp: { label: 'C#', class: 'csharpBadge', icon: faCode },
    dotnet: { label: '.NET', class: 'dotnetBadge', icon: faCode },
    docker: { label: 'Docker', class: 'dockerBadge', icon: faCode },
    rest: { label: 'REST API', class: 'restBadge', icon: faServer },
    html: { label: 'HTML', class: 'htmlBadge', icon: faCode },
    css: { label: 'CSS', class: 'cssBadge', icon: faCode },
  };

  if (icons[i]) {
    const icon = icons[i];

    // If it has an image source, use image
    if (icon.src) {
      return (
        <span className={`badge ${icon.class} m-1`} key={icon.label}>
          <img alt={icon.label} className="codeIcon" src={icon.src} />
          {icon.label}
        </span>
      );
    }

    // Otherwise use Font Awesome icon
    return (
      <span className={`badge ${icon.class} m-1`} key={icon.label}>
        <FontAwesomeIcon icon={icon.icon} style={{ marginRight: '0.5rem' }} />
        {icon.label}
      </span>
    );
  }

  // Fallback for unknown skills
  return (
    <span className="badge m-1" key={skill}>
      <FontAwesomeIcon icon={faCode} style={{ marginRight: '0.5rem' }} />
      {skill}
    </span>
  );
}

// The ProjectCard component

const ProjectCard = ({ project }) => {
  const [likes, setLikes] = React.useState(project.likes);
  const [floatingPlus, setFloatingPlus] = React.useState(null);
  const buttonRef = React.useRef(null);

  // Reflects the server-fetched count once it arrives after initial mount.
  React.useEffect(() => {
    setLikes(project.likes);
  }, [project.likes]);

  const handleLike = (e) => {
    setLikes((prev) => prev + 1);

    // Create floating + effect
    const rect = buttonRef.current.getBoundingClientRect();
    const randomId = Math.random();

    setFloatingPlus((prev) => [
      ...(prev || []),
      {
        id: randomId,
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      },
    ]);

    // Remove floating + after animation
    setTimeout(() => {
      setFloatingPlus((prev) => prev.filter((item) => item.id !== randomId));
    }, 600);

    likeProject(project.id)
      .then((data) => setLikes(data.likes))
      .catch(() => setLikes((prev) => prev - 1));
  };

  return (
    <div className="project-card os-project-card m-2">
      <img
        className="card-img-top project-img os-project-img"
        src={project.imgUrl}
        alt={project.name}
      />
      <div className="card-body project-card-body os-project-info">
        <div className="card-top">
          <div className="project-title os-project-title">{project.name}</div>
        </div>
        <p className="card-text project-description os-project-desc">
          {project.description}
        </p>
        <hr className="hrStyle1" />
        <div className="d-flex justify-content-center">
          <div id="icons">
            {project.skillsUsed.map((skill) => getIcon(skill))}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-center os-project-links">
        <button
          ref={buttonRef}
          className="btn os-btn m-1 like-button"
          id={project.id}
          aria-label={`Like ${project.name}`}
          onClick={handleLike}
          style={{ position: 'relative' }}
        >
          {likes} <FontAwesomeIcon icon={faHeart} />
          {floatingPlus &&
            floatingPlus.map((plus) => (
              <span
                key={plus.id}
                className="floating-plus"
                style={{
                  position: 'fixed',
                  left: plus.x,
                  top: plus.y,
                  pointerEvents: 'none',
                }}
              >
                +
              </span>
            ))}
        </button>
        {project.liveBool && (
          <a
            href={project.liveUrl}
            className="btn os-btn m-1"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Demo for ${project.name}`}
          >
            Demo <FontAwesomeIcon icon={faLaptop} />
          </a>
        )}

        {project.sourceBool && (
          <a
            href={project.codeUrl}
            className="btn os-btn m-1"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Source for ${project.name}`}
          >
            Source <FontAwesomeIcon icon={faCode} />
          </a>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
